import { ReactNode, forwardRef, useCallback, useEffect } from 'react';

import ResizeObserver from 'lib/resize-observer';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { ScrollPosition } from './activity-preview.hooks';
import * as Styled from './activity-preview.style';

export const ActivityThumbnailButton = ({
  isNext,
  onClick,
}: {
  isNext?: boolean;
  onClick: () => void;
}) => (
  <Styled.ActivityThumbnailButton
    type="button"
    $isNext={isNext}
    aria-label={`Show ${isNext ? 'next' : 'previous'} thumbnail`}
    onClick={onClick}
    data-selector={`${isNext ? 'move-next' : 'move-previous'}`}
  >
    <InlineSvg
      id="icon_arrow_left"
      width={16}
      height={16}
      $rotate={isNext ? 180 : 0}
    />
  </Styled.ActivityThumbnailButton>
);

export const ActivityCarouselButton = ({
  isNext,
  onClick,
}: {
  isNext?: boolean;
  onClick: () => void;
}) => (
  <Styled.ActivityCarouselButton
    type="button"
    $isNext={isNext}
    aria-label={`Show ${isNext ? 'next' : 'previous'} image preview`}
    onClick={onClick}
  >
    <InlineSvg
      id="icon_arrow_left"
      width={16}
      height={16}
      $rotate={isNext ? 180 : 0}
    />
  </Styled.ActivityCarouselButton>
);

export const ActivityThumbnailScroll = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    handleOnScroll: () => void;
    setScrollPosition: (position: ScrollPosition) => void;
  }
>(({ children, handleOnScroll, setScrollPosition }, ref) => {
  const checkIfScrollBarNeedsArrows = useCallback(() => {
    if (ref && typeof ref !== 'function' && ref.current) {
      if (ref.current.scrollWidth <= ref.current.clientWidth) {
        ref.current.scrollLeft = 0;
        setScrollPosition({
          start: true,
          end: true,
        });
      } else {
        setScrollPosition({
          start: true,
          end: false,
        });
      }
    }
  }, [ref, setScrollPosition]);

  useEffect(() => {
    const copyOfRef = ref;
    if (copyOfRef && typeof copyOfRef !== 'function' && copyOfRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        checkIfScrollBarNeedsArrows();
      });
      resizeObserver.observe(copyOfRef.current);
      return () => {
        if (copyOfRef.current) {
          resizeObserver.unobserve(copyOfRef.current);
        }
      };
    }
  }, [ref, checkIfScrollBarNeedsArrows]);

  return (
    <Styled.ActivityThumbnailsScroll
      ref={ref}
      onScroll={handleOnScroll}
      data-selector="thumbnail-carousel"
    >
      {children}
    </Styled.ActivityThumbnailsScroll>
  );
});
